import { useEffect, useState } from 'react'

import styles from './verificationModal.module.scss'

import { DomainVerificationRecord, DomainVerificationStatus } from '@/api/generated/api'
import Button from '@/components/Button'
import DialogWrapper from '@/components/Dialog'
import { useToast } from '@/components/Toast/ToastContext'
import { compactAmPm } from '@/utils/compactAmPm'

interface VerificationModalProps {
  isOpen: boolean
  onClose: () => void
  domain: DomainVerificationStatus
  record: DomainVerificationRecord | null
  isInitiatePending: boolean
  onCheck: () => void
  isCheckPending: boolean
  isAdmin: boolean
}

const formatLastChecked = (domain: DomainVerificationStatus): string | null => {
  if (!domain.last_verification_check_at || domain.verification_attempts === 0) return null
  return `Last checked ${compactAmPm(new Date(domain.last_verification_check_at).toLocaleString())}`
}

const VerificationModal = ({
  isOpen,
  onClose,
  domain,
  record,
  isInitiatePending,
  onCheck,
  isCheckPending,
  isAdmin,
}: VerificationModalProps) => {
  const { updateToast } = useToast()
  const [copiedHost, setCopiedHost] = useState(false)
  const [copiedValue, setCopiedValue] = useState(false)

  useEffect(() => {
    if (!isOpen) {
      setCopiedHost(false)
      setCopiedValue(false)
    }
  }, [isOpen])

  const copyToClipboard = async (text: string, kind: 'host' | 'value') => {
    try {
      await navigator.clipboard.writeText(text)
      updateToast('Copied to clipboard', 'success')
      if (kind === 'host') {
        setCopiedHost(true)
        window.setTimeout(() => setCopiedHost(false), 2000)
      } else {
        setCopiedValue(true)
        window.setTimeout(() => setCopiedValue(false), 2000)
      }
    } catch {
      updateToast('Could not copy', 'error')
    }
  }

  const lastChecked = formatLastChecked(domain)

  return (
    <DialogWrapper
      isOpen={isOpen}
      close={onClose}
      title={`Verify ${domain.domain}`}
      subtitle="Add a TXT record to your domain's DNS, wait for it to propagate, then confirm here."
      minWidth={560}
    >
      <ol className={styles.steps}>
        <li>
          <span className={styles.stepLabel}>Start verification.</span> Wispr generates a unique TXT record (Host and
          Value) for this domain.
        </li>
        <li>
          <span className={styles.stepLabel}>Add the TXT record to your DNS provider.</span> Sign in to your DNS
          provider (e.g., Cloudflare, GoDaddy, Namecheap, Route 53), open the DNS settings for the domain, and add a new{' '}
          <strong>TXT</strong> record using the exact Host and Value below. Save the change.
        </li>
        <li>
          <span className={styles.stepLabel}>Wait, then check verification.</span> DNS changes can take up to an hour
          (sometimes longer) to propagate globally. Once propagated, click <strong>Check verification</strong>. If it
          doesn&apos;t pass yet, wait a few more minutes and try again.
        </li>
      </ol>

      {record ? (
        <div className={styles.recordBox}>
          <div className={styles.recordLabel}>TXT record</div>
          <div className={styles.codeRow}>
            <pre className={styles.code}>{record.host}</pre>
            <Button
              title={copiedHost ? 'Copied' : 'Copy host'}
              type="light"
              onClick={() => copyToClipboard(record.host, 'host')}
              isDisabled={isCheckPending}
              className={styles.copyButton}
            />
          </div>
          <div className={styles.codeRow}>
            <pre className={styles.code}>{record.value}</pre>
            <Button
              title={copiedValue ? 'Copied' : 'Copy value'}
              type="light"
              onClick={() => copyToClipboard(record.value, 'value')}
              isDisabled={isCheckPending}
              className={styles.copyButton}
            />
          </div>
        </div>
      ) : (
        <div className={styles.recordPlaceholder}>
          {isInitiatePending ? 'Generating TXT record…' : 'TXT record will appear here.'}
        </div>
      )}

      {lastChecked && <div className={styles.lastChecked}>{lastChecked}</div>}

      <div className={styles.actions}>
        <Button title="Close" type="light" onClick={onClose} isDisabled={isCheckPending} />
        <Button
          title="Check verification"
          type="dark"
          onClick={onCheck}
          isLoading={isCheckPending}
          isDisabled={!isAdmin || !record}
        />
      </div>
    </DialogWrapper>
  )
}

export default VerificationModal
